
import React, { useEffect, useMemo, useState } from 'react';
import { SubmissionData, Aggregator, Brand, ItemDraft } from '../../../types';
import { SubmissionStatus } from '../../../enums';
import { TrendingUp, DollarSign, ShoppingBag, CheckCircle, BarChart2, PieChart, ArrowUpRight, Calendar, Users, Layout, Award, Package, Globe, ArrowUp, ArrowDown, Percent, Megaphone, Activity } from 'lucide-react';
import { apiClient } from '../../../services/apiClient';

// ============= HELPER FUNCTIONS FOR APPROVED DATA =============

/** Get only approved submissions (case-insensitive status check) */
const getApprovedSubmissions = (submissions: SubmissionData[]): SubmissionData[] => {
    return (submissions || []).filter((s) => {
        if (!s || !s.status) return false;
        const status = String(s.status).toUpperCase();
        return status === 'APPROVED' || status === SubmissionStatus.APPROVED.toUpperCase();
    });
};

/** Extract all items from submissions */
const extractItems = (submissions: SubmissionData[]): (ItemDraft & { campaignName?: string; submissionId?: string })[] => {
    const items: (ItemDraft & { campaignName?: string; submissionId?: string })[] = [];
    submissions.forEach((s) => {
        if (!s.items || !Array.isArray(s.items)) return;
        s.items.forEach((item) => {
            if (item && item.id && item.name) {
                items.push({ ...item, campaignName: s.campaignName, submissionId: s.submissionId });
            }
        });
    });
    return items;
};

/** Extract aggregator usage counts from items */
const extractAggregatorCounts = (items: ItemDraft[]): Record<string, number> => {
    const counts: Record<string, number> = {};
    items.forEach((item) => {
        (item.selectedAggregators || []).forEach((agg) => {
            counts[agg] = (counts[agg] || 0) + 1;
        });
    });
    return counts;
};

/** Group submissions by campaign */
const groupByCampaign = (submissions: SubmissionData[]): Record<string, SubmissionData[]> => {
    const grouped: Record<string, SubmissionData[]> = {};
    submissions.forEach((s) => {
        const key = s.campaignName || s.campaignId || 'Unknown Campaign';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(s);
    });
    return grouped;
};

interface TopCampaign {
    name: string;
    submissionCount: number;
    brands: string[];
    dateRange: { start: string; end: string };
}

interface TopItem {
    name: string;
    count: number;
    avgPrice: number;
    minPrice: number;
    maxPrice: number;
}

interface TopAggregator {
    name: string;
    count: number;
}

interface PriceMovement {
    upCount: number;
    downCount: number;
    avgChange: number;
    avgPercentChange: number;
}

interface CampaignTableRow {
    campaignName: string;
    brands: string[];
    discountDisplay: string;
    fundingDisplay: string;
    approvedSubmissions: number;
    totalItems: number;
    topAggregator: string;
}

interface AnalyticsSummary {
    submissionsByStatus: { status: string; count: number }[];
    campaignsByStatus: { status: string; count: number }[];
    brands: { brand: string; count: number }[];
    platforms: { aggregator: string; count: number }[];
    managers: { managerId: string; managerName: string; submissions: number; items: number }[];
    fundingImpact?: {
        discountTypeBreakdown: Record<string, number>;
        fundingTypeBreakdown: Record<string, number>;
        sterlingTotal: number;
        cofundTotal: number;
        campaignCount: number;
        discountTypeSummary?: {
            discountType: string;
            campaignCount: number;
            averageDiscountValue: number;
        }[];
    };
}

interface AnalyticsViewProps {
    submissions?: SubmissionData[];
    aggregators?: Aggregator[];
    currencyCode?: string;
    brands?: Brand[];
}

export const AnalyticsView: React.FC<AnalyticsViewProps> = ({ submissions, aggregators = [], currencyCode, brands = [] }) => {
    const currency = currencyCode || 'QAR';
    const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview');
    const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [brandFilter, setBrandFilter] = useState<string[]>([]);
    const [transitioning, setTransitioning] = useState(false);

    const brandLookup = useMemo(() => {
        const map = new Map<string, string>();
        brands.forEach((b) => map.set(b.id, b.name));
        return map;
    }, [brands]);

    const localFallback = useMemo<AnalyticsSummary | null>(() => {
        if (!submissions) return null;
        const submissionsByStatus: Record<string, number> = {};
        const brandCounts: Record<string, number> = {};
        const platformCounts: Record<string, number> = {};
        const managerMap: Record<string, { managerId: string; managerName: string; submissions: number; items: number }> = {};
        const allowedBrandNames = new Set(
            (brandFilter.length ? brandFilter : []).map((id) => (brandLookup.get(id) || '').toLowerCase()).filter(Boolean)
        );

        submissions.forEach((s) => {
            if (s.type === 'Proposal') return;
            if (allowedBrandNames.size) {
                const hasMatch = (s.brands || []).some((b) => allowedBrandNames.has(String(b).toLowerCase()));
                if (!hasMatch) return;
            }
            submissionsByStatus[s.status || SubmissionStatus.PENDING] =
                (submissionsByStatus[s.status || SubmissionStatus.PENDING] || 0) + 1;
            s.brands.forEach((b) => { brandCounts[b] = (brandCounts[b] || 0) + s.items.length; });
            s.items.forEach((i) => i.selectedAggregators.forEach((agg) => {
                platformCounts[agg] = (platformCounts[agg] || 0) + 1;
            }));
            const id = s.manager.fileNumber;
            if (!managerMap[id]) managerMap[id] = { managerId: id, managerName: s.manager.name, submissions: 0, items: 0 };
            managerMap[id].submissions += 1;
            managerMap[id].items += s.items.length;
        });

        return {
            submissionsByStatus: Object.entries(submissionsByStatus).map(([status, count]) => ({ status, count })),
            campaignsByStatus: [], // not derived locally
            brands: Object.entries(brandCounts).map(([brand, count]) => ({ brand, count })),
            platforms: Object.entries(platformCounts).map(([aggregator, count]) => ({ aggregator, count })),
            managers: Object.values(managerMap),
        };
    }, [submissions, brandFilter, brandLookup]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            // Only show full loading state on initial load
            if (!summary) setLoading(true);
            setError(null);
            try {
                const data = await apiClient.analytics.summary({ brandIds: brandFilter });
                if (!cancelled) setSummary(data as AnalyticsSummary);
            } catch (err: any) {
                console.error('Failed to load analytics', err);
                if (!cancelled) {
                    setError('Analytics API unavailable. Showing local data where possible.');
                    if (localFallback) setSummary(localFallback);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [localFallback, brandFilter, brandLookup]);

    const normalizedSummary = useMemo(() => {
        if (!summary) return null;
        return {
            submissionsByStatus: (summary.submissionsByStatus || []).map((r) => ({ ...r, count: Number((r as any).count || 0) })),
            campaignsByStatus: (summary.campaignsByStatus || []).map((r) => ({ ...r, count: Number((r as any).count || 0) })),
            brands: (summary.brands || []).map((r) => ({ ...r, count: Number((r as any).count || 0) })),
            platforms: (summary.platforms || []).map((r) => ({ ...r, count: Number((r as any).count || 0) })),
            managers: (summary.managers || []).map((r) => ({
                ...r,
                submissions: Number((r as any).submissions || 0),
                items: Number((r as any).items || 0),
            })),
            fundingImpact: summary.fundingImpact || {
                discountTypeBreakdown: {},
                fundingTypeBreakdown: {},
                sterlingTotal: 0,
                cofundTotal: 0,
                discountTypeSummary: [],
                campaignCount: 0,
            },
        };
    }, [summary]);

    const statusCounts = normalizedSummary?.submissionsByStatus || [];
    const totalSubmissions = statusCounts.reduce((acc, row) => acc + (row.count || 0), 0);
    const totalItems = (normalizedSummary?.managers || []).reduce((acc, row) => acc + (row.items || 0), 0) || 0;
    const totalValue = useMemo(() => {
        if (submissions && submissions.length > 0) {
            return submissions.reduce((acc, sub) => {
                const itemSum = sub.items.reduce((sum, i) => sum + (parseFloat(i.sellingPrice || '0') || 0), 0);
                return acc + itemSum;
            }, 0);
        }
        return 0;
    }, [submissions]);
    const projectedDiscount = totalValue * 0.30;
    const approvedCount =
        statusCounts.find((r) => r.status === SubmissionStatus.APPROVED)?.count || 0;
    const approvalRate = totalSubmissions > 0 ? (approvedCount / totalSubmissions) * 100 : 0;
    const fundingImpact = normalizedSummary?.fundingImpact;

    // ============= APPROVED-ONLY INSIGHTS DATA =============
    const approvedSubmissions = useMemo(() => getApprovedSubmissions(submissions || []), [submissions]);
    const approvedItems = useMemo(() => extractItems(approvedSubmissions), [approvedSubmissions]);
    const approvedAggregatorCounts = useMemo(() => extractAggregatorCounts(approvedItems), [approvedItems]);
    const approvedByCampaign = useMemo(() => groupByCampaign(approvedSubmissions), [approvedSubmissions]);

    // Top 3 Campaigns by submission count (approved only)
    const topCampaigns = useMemo<TopCampaign[]>(() => {
        const entries = Object.entries(approvedByCampaign).map(([name, subs]) => {
            const allBrands = new Set<string>();
            let minDate: string | null = null;
            let maxDate: string | null = null;
            subs.forEach((s) => {
                (s.brands || []).forEach((b) => allBrands.add(b));
                if (s.timestamp) {
                    if (!minDate || s.timestamp < minDate) minDate = s.timestamp;
                    if (!maxDate || s.timestamp > maxDate) maxDate = s.timestamp;
                }
            });
            return {
                name,
                submissionCount: subs.length,
                brands: Array.from(allBrands),
                dateRange: { start: minDate || '', end: maxDate || '' },
            };
        });
        return entries.sort((a, b) => b.submissionCount - a.submissionCount).slice(0, 3);
    }, [approvedByCampaign]);

    // Top 3 Items most submitted (approved only)
    const topItems = useMemo<TopItem[]>(() => {
        const itemMap: Record<string, { count: number; prices: number[] }> = {};
        approvedItems.forEach((item) => {
            const key = item.name.toLowerCase().trim();
            if (!itemMap[key]) itemMap[key] = { count: 0, prices: [] };
            itemMap[key].count += 1;
            const price = parseFloat(item.sellingPrice || '0') || 0;
            if (price > 0) itemMap[key].prices.push(price);
        });
        return Object.entries(itemMap)
            .map(([name, data]) => {
                const prices = data.prices;
                const avg = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
                const min = prices.length > 0 ? Math.min(...prices) : 0;
                const max = prices.length > 0 ? Math.max(...prices) : 0;
                return { name, count: data.count, avgPrice: avg, minPrice: min, maxPrice: max };
            })
            .sort((a, b) => b.count - a.count)
            .slice(0, 3);
    }, [approvedItems]);

    // Top 3 Aggregators (approved only)
    const topAggregators = useMemo<TopAggregator[]>(() => {
        return Object.entries(approvedAggregatorCounts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 3);
    }, [approvedAggregatorCounts]);

    // Price Movement (approved only)
    const priceMovement = useMemo<PriceMovement>(() => {
        let upCount = 0;
        let downCount = 0;
        const changes: number[] = [];
        const percentChanges: number[] = [];
        approvedItems.forEach((item) => {
            const original = parseFloat(item.originalPrice || '0') || 0;
            const selling = parseFloat(item.sellingPrice || '0') || 0;
            if (original > 0 && selling > 0) {
                const diff = selling - original;
                if (diff > 0) upCount++;
                else if (diff < 0) downCount++;
                changes.push(diff);
                percentChanges.push((diff / original) * 100);
            }
        });
        const avgChange = changes.length > 0 ? changes.reduce((a, b) => a + b, 0) / changes.length : 0;
        const avgPercentChange = percentChanges.length > 0 ? percentChanges.reduce((a, b) => a + b, 0) / percentChanges.length : 0;
        return { upCount, downCount, avgChange, avgPercentChange };
    }, [approvedItems]);

    // Campaign Table Data (approved only)
    const campaignTableData = useMemo<CampaignTableRow[]>(() => {
        return Object.entries(approvedByCampaign).map(([campaignName, subs]) => {
            const allBrands = new Set<string>();
            let totalItemCount = 0;
            const aggCounts: Record<string, number> = {};
            subs.forEach((s) => {
                (s.brands || []).forEach((b) => allBrands.add(b));
                (s.items || []).forEach((item) => {
                    totalItemCount++;
                    (item.selectedAggregators || []).forEach((agg) => {
                        aggCounts[agg] = (aggCounts[agg] || 0) + 1;
                    });
                });
            });
            const topAgg = Object.entries(aggCounts).sort((a, b) => b[1] - a[1])[0];
            return {
                campaignName,
                brands: Array.from(allBrands),
                discountDisplay: '—', // Would need campaign data
                fundingDisplay: '—', // Would need campaign data
                approvedSubmissions: subs.length,
                totalItems: totalItemCount,
                topAggregator: topAgg ? topAgg[0] : '—',
            };
        }).sort((a, b) => b.approvedSubmissions - a.approvedSubmissions);
    }, [approvedByCampaign]);

    const donutSegments = statusCounts.map((row) => {
        const color =
            row.status === SubmissionStatus.PENDING ? '#fbbf24' :
            row.status === SubmissionStatus.APPROVED ? '#34d399' :
            row.status === SubmissionStatus.EXPORTED ? '#60a5fa' :
            row.status === SubmissionStatus.REJECTED ? '#f87171' : '#a1a1aa';
        const percent = totalSubmissions > 0 ? (row.count / totalSubmissions) * 100 : 0;
        return { label: row.status, value: row.count, color, percent };
    });

    let currentAngle = 0;
    const gradientParts = donutSegments.map(seg => {
        const start = currentAngle;
        const end = currentAngle + seg.percent;
        currentAngle = end;
        return `${seg.color} ${start}% ${end}%`;
    });
    const conicGradient = `conic-gradient(${gradientParts.join(', ')})`;

    const sortedBrands = (normalizedSummary?.brands || []).slice().sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 5);
    const maxBrandCount = sortedBrands[0]?.count || 1;

    const platformCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        if (normalizedSummary?.platforms?.length) {
            normalizedSummary.platforms.forEach((p) => {
                counts[p.aggregator] = Number(p.count || 0);
            });
        } else if (submissions) {
            submissions.forEach((s) =>
                s.items.forEach((i) =>
                    i.selectedAggregators.forEach((agg) => {
                        counts[agg] = (counts[agg] || 0) + 1;
                    })
                )
            );
        }
        return counts;
    }, [normalizedSummary?.platforms, submissions]);

    const totalPlatformTags = Object.values(platformCounts).reduce((acc, n) => acc + n, 0);

    const trendData = useMemo(() => {
        const isSameDay = (d1: Date, d2: Date) =>
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();

        const last7 = [...Array(7)].map((_, i) => {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() - (6 - i));
            return d;
        });

        const counts = last7.map((day) => {
            const count = (submissions || []).filter((s) => {
                if (!s.timestamp) return false;
                const subDate = new Date(s.timestamp);
                if (isNaN(subDate.getTime())) return false;
                return isSameDay(subDate, day);
            }).length;
            return {
                key: day.toISOString(),
                day: day.toLocaleDateString(undefined, { day: 'numeric' }),
                short: day.toLocaleDateString(undefined, { weekday: 'short' }),
                count,
            };
        });

        if (counts.every((c) => c.count === 0) && totalSubmissions > 0) {
            counts[counts.length - 1].count = totalSubmissions;
        }

        return counts;
    }, [submissions, totalSubmissions]);

    const maxTrend = Math.max(1, ...trendData.map((t) => t.count || 0));

    const leaderboard = useMemo(() => {
        if (summary?.managers?.length) {
            return summary.managers
                .map((m) => ({
                    name: m.managerName,
                    submissions: Number(m.submissions || 0),
                    totalItems: Number(m.items || 0),
                    efficiency:
                        m.submissions && m.submissions > 0
                            ? Math.min(100, ((Number(m.items || 0) / Number(m.submissions)) * 10) + 50)
                            : 0,
                }))
                .sort((a, b) => b.submissions - a.submissions);
        }

        if (submissions?.length) {
            const map: Record<string, { name: string; submissions: number; totalItems: number }> = {};
            submissions.forEach((s) => {
                const id = s.manager.fileNumber;
                if (!map[id]) map[id] = { name: s.manager.name, submissions: 0, totalItems: 0 };
                map[id].submissions += 1;
                map[id].totalItems += s.items.length;
            });
            return Object.values(map)
                .map((m) => ({
                    ...m,
                    efficiency: m.submissions ? Math.min(100, ((m.totalItems / m.submissions) * 10) + 50) : 0,
                }))
                .sort((a, b) => b.submissions - a.submissions);
        }

        return [];
    }, [summary?.managers, submissions]);

    if (loading) {
        return <div className="p-6 text-sm text-zinc-500 animate-pulse">Loading analytics...</div>;
    }

    const TabButton = ({ id, label, icon: Icon }: { id: AnalyticsTab; label: string; icon: any }) => (
        <button
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all border ${
                activeTab === id
                    ? 'bg-zinc-900 border-zinc-900 text-white shadow-lg shadow-zinc-200'
                    : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 hover:border-zinc-300'
            }`}
        >
            <Icon className="w-4 h-4" />
            {label}
        </button>
    );

    return (
        <div className="animate-fade-in space-y-8 w-full">
            {/* Header & Tabs */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 bg-teal-50 rounded-xl border border-teal-100">
                            <BarChart2 className="w-6 h-6 text-teal-600" />
                        </div>
                        <h1 className="text-3xl font-extrabold text-zinc-900 tracking-tight">Analytics Dashboard</h1>
                    </div>
                    <p className="text-zinc-500 text-sm ml-[52px]">
                        Real-time insights on submission trends, revenue, and performance.
                    </p>
                </div>
                <div className="flex items-center gap-1 bg-white p-1.5 rounded-2xl border border-zinc-200 shadow-lg shadow-zinc-200/30">
                    <TabButton id="overview" label="Overview" icon={Layout} />
                    <TabButton id="performance" label="Performance" icon={BarChart2} />
                    <TabButton id="financials" label="Financials" icon={DollarSign} />
                </div>
            </div>

            {/* Brand Filter */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-lg shadow-zinc-200/30">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <p className="text-xs font-bold text-zinc-500 uppercase">Brand Filters</p>
                        <p className="text-[11px] text-zinc-400">Apply brand scope to analytics and reports.</p>
                    </div>
                    <button
                      className="text-xs font-bold text-teal-600 hover:text-teal-700"
                      onClick={() => setBrandFilter([])}
                    >
                      Clear
                    </button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {brands.map((b) => {
                        const checked = brandFilter.includes(b.id);
                        return (
                            <button
                              key={b.id}
                              onClick={() => {
                                  const next = checked
                                    ? brandFilter.filter((id) => id !== b.id)
                                    : [...brandFilter, b.id];
                                  setTransitioning(true);
                                  setBrandFilter(next);
                                  requestAnimationFrame(() => {
                                      requestAnimationFrame(() => setTransitioning(false));
                                  });
                                }}
                              className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${checked ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-200 hover:text-teal-700'}`}
                            >
                              {b.name}
                            </button>
                        );
                    })}
                    {brands.length === 0 && <span className="text-xs text-zinc-500">No brands available.</span>}
                </div>
            </div>

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
                <div className={`space-y-6 transition-all duration-200 ${transitioning ? 'opacity-50 scale-[0.995]' : 'opacity-100 scale-100'}`}>
                    
                    {/* Hero Section */}
                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                        {/* System Status Card */}
                        <div className="xl:col-span-1 bg-white rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 overflow-hidden flex flex-col">
                            <div className="bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 p-6 relative overflow-hidden shrink-0">
                                <div className="absolute inset-0 opacity-10">
                                    <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/20"></div>
                                    <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full bg-white/10"></div>
                                </div>
                                <div className="relative z-10 flex flex-col items-center text-center">
                                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 ring-4 ring-white/30">
                                        <Activity className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white">System Status</h3>
                                    <p className="text-teal-100 text-sm font-medium mt-1">Live Data Stream</p>
                                </div>
                            </div>
                            <div className="p-6 flex-1 flex flex-col gap-4">
                                <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                                    <span className="text-xs font-bold text-zinc-500 uppercase">Active Brands</span>
                                    <span className="text-sm font-black text-zinc-800">{brands.length}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                                    <span className="text-xs font-bold text-zinc-500 uppercase">Aggregators</span>
                                    <span className="text-sm font-black text-zinc-800">{aggregators.filter(a => a.active).length}</span>
                                </div>
                                <div className="mt-auto pt-4 border-t border-zinc-100">
                                    <p className="text-[10px] text-zinc-400 text-center">
                                        Last updated: {new Date().toLocaleTimeString()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* KPI Grid */}
                        <div className="xl:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Value KPI */}
                            <div className="bg-white p-6 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 relative overflow-hidden group hover:border-teal-200 transition-all">
                                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <DollarSign className="w-24 h-24 text-teal-500 transform rotate-12 translate-x-4 -translate-y-4" />
                                </div>
                                <div className="flex justify-between items-start mb-4 relative z-10">
                                    <div className="p-3 bg-teal-50 text-teal-600 rounded-2xl group-hover:bg-teal-100 transition-colors">
                                        <DollarSign className="w-6 h-6" />
                                    </div>
                                    <span className="flex items-center text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                                        <ArrowUpRight className="w-3 h-3 mr-1" /> Live
                                    </span>
                                </div>
                                <div className="relative z-10">
                                    <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Total Item Value</p>
                                    <h3 className="text-3xl font-black text-zinc-900 mt-2 tracking-tight">
                                        {currency} {totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </h3>
                                </div>
                            </div>

                            {/* Discount Impact KPI */}
                            <div className="bg-white p-6 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 relative overflow-hidden group hover:border-pink-200 transition-all">
                                <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <TrendingUp className="w-24 h-24 text-pink-500 transform rotate-12 translate-x-4 -translate-y-4" />
                                </div>
                                <div className="flex justify-between items-start mb-4 relative z-10">
                                    <div className="p-3 bg-pink-50 text-pink-600 rounded-2xl group-hover:bg-pink-100 transition-colors">
                                        <TrendingUp className="w-6 h-6" />
                                    </div>
                                </div>
                                <div className="relative z-10">
                                    <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Projected Discount</p>
                                    <h3 className="text-3xl font-black text-zinc-900 mt-2 tracking-tight">
                                        {currency} {projectedDiscount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </h3>
                                    <p className="text-xs text-zinc-400 mt-1 font-medium">Estimated ~30% impact</p>
                                </div>
                            </div>

                            {/* Total Items */}
                            <div className="bg-white p-6 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 group hover:border-blue-200 transition-all">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl group-hover:bg-blue-100 transition-colors">
                                        <ShoppingBag className="w-6 h-6" />
                                    </div>
                                </div>
                                <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Total Items</p>
                                <h3 className="text-3xl font-black text-zinc-900 mt-2 tracking-tight">{totalItems}</h3>
                            </div>

                            {/* Approval Rate */}
                            <div className="bg-white p-6 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 group hover:border-emerald-200 transition-all">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl group-hover:bg-emerald-100 transition-colors">
                                        <CheckCircle className="w-6 h-6" />
                                    </div>
                                </div>
                                <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Approval Rate</p>
                                <h3 className="text-3xl font-black text-zinc-900 mt-2 tracking-tight">{approvalRate.toFixed(0)}%</h3>
                            </div>
                        </div>
                    </div>

                    {/* CHARTS ROW */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Daily Trend */}
                        <div className="lg:col-span-2 bg-white p-8 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 flex flex-col">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="bg-zinc-50 p-3 rounded-2xl text-zinc-900 border border-zinc-100">
                                    <Calendar className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-zinc-900 text-lg">Submission Trends</h3>
                                    <p className="text-xs text-zinc-400 font-bold uppercase tracking-wider mt-0.5">Live Snapshot</p>
                                </div>
                            </div>

                            <div className="flex-1 flex items-end justify-between gap-4 h-64 px-4 pb-2">
                                {trendData.map((d, i) => {
                                    const heightPx = d.count > 0 ? Math.max(12, (d.count / maxTrend) * 200) : 0;
                                    return (
                                        <div key={i} className="flex flex-col items-center gap-3 flex-1 group cursor-pointer">
                                            <div className="relative w-full bg-zinc-50/50 rounded-t-2xl flex items-end h-full group-hover:bg-zinc-50 transition-colors overflow-hidden">
                                                <div 
                                                    className="w-full bg-zinc-900 rounded-t-2xl transition-all duration-1000 ease-out group-hover:bg-teal-500 relative min-h-[4px]"
                                                    style={{ height: `${heightPx}px` }}
                                                >
                                                    {/* Tooltip */}
                                                    <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-xs font-bold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 whitespace-nowrap shadow-xl z-20">
                                                        {d.count} Submissions
                                                        <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-zinc-900 rotate-45"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-xs font-bold text-zinc-400 group-hover:text-zinc-900 transition-colors">{d.short}</div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Status Donut */}
                        <div className="bg-white p-8 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 flex flex-col">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="bg-zinc-50 p-3 rounded-2xl text-zinc-900 border border-zinc-100">
                                    <PieChart className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-zinc-900 text-lg">Status Overview</h3>
                            </div>

                            <div className="flex-1 flex items-center justify-center relative py-4">
                                <div 
                                    className="w-56 h-56 rounded-full relative transition-all duration-1000 shadow-2xl shadow-zinc-200/50"
                                    style={{ background: conicGradient }}
                                >
                                    <div className="absolute inset-6 bg-white rounded-full flex flex-col items-center justify-center shadow-inner">
                                        <span className="text-4xl font-black text-zinc-900 tracking-tight">{totalSubmissions}</span>
                                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mt-1">Total Requests</span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mt-8">
                                {donutSegments.map(seg => (
                                    <div key={seg.label} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 transition-colors">
                                        <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: seg.color }} />
                                        <div className="text-xs font-bold text-zinc-600 flex-1">{seg.label}</div>
                                        <div className="text-xs font-bold text-zinc-900 bg-zinc-100 px-2 py-0.5 rounded-md">{seg.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ============= INSIGHTS SECTION (Approved Only) ============= */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
                                <Award className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-zinc-900">Approved Insights</h3>
                                <p className="text-xs text-zinc-400">Top performers from approved submissions only</p>
                            </div>
                        </div>

                        {/* Top 3 Cards Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Top 3 Campaigns */}
                            <div className="bg-white p-6 rounded-2xl border border-zinc-100 shadow-lg shadow-zinc-200/30">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                                        <Megaphone className="w-4 h-4" />
                                    </div>
                                    <p className="text-sm font-bold text-zinc-700">Top Campaigns</p>
                                </div>
                                <div className="space-y-3">
                                    {topCampaigns.length === 0 ? (
                                        <p className="text-sm text-zinc-400 italic">No approved campaigns yet</p>
                                    ) : (
                                        topCampaigns.map((c, idx) => (
                                            <div key={c.name} className="flex items-start gap-3 p-3 bg-zinc-50 rounded-xl">
                                                <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold shrink-0">
                                                    {idx + 1}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-zinc-800 truncate">{c.name}</p>
                                                    <p className="text-xs text-zinc-500">{c.submissionCount} submissions</p>
                                                    <p className="text-[10px] text-zinc-400 truncate">{c.brands.slice(0, 2).join(', ')}{c.brands.length > 2 ? ` +${c.brands.length - 2}` : ''}</p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Top 3 Items */}
                            <div className="bg-white p-6 rounded-2xl border border-zinc-100 shadow-lg shadow-zinc-200/30">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-amber-50 text-amber-600 rounded-xl">
                                        <Package className="w-4 h-4" />
                                    </div>
                                    <p className="text-sm font-bold text-zinc-700">Top Items</p>
                                </div>
                                <div className="space-y-3">
                                    {topItems.length === 0 ? (
                                        <p className="text-sm text-zinc-400 italic">No approved items yet</p>
                                    ) : (
                                        topItems.map((item, idx) => (
                                            <div key={item.name} className="flex items-start gap-3 p-3 bg-zinc-50 rounded-xl">
                                                <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold shrink-0">
                                                    {idx + 1}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-zinc-800 truncate capitalize">{item.name}</p>
                                                    <p className="text-xs text-zinc-500">{item.count} occurrences</p>
                                                    <p className="text-[10px] text-zinc-400">
                                                        Avg: {currency} {item.avgPrice.toFixed(2)} | Range: {item.minPrice.toFixed(0)}-{item.maxPrice.toFixed(0)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Top 3 Aggregators */}
                            <div className="bg-white p-6 rounded-2xl border border-zinc-100 shadow-lg shadow-zinc-200/30">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
                                        <Globe className="w-4 h-4" />
                                    </div>
                                    <p className="text-sm font-bold text-zinc-700">Top Aggregators</p>
                                </div>
                                <div className="space-y-3">
                                    {topAggregators.length === 0 ? (
                                        <p className="text-sm text-zinc-400 italic">No aggregator data yet</p>
                                    ) : (
                                        topAggregators.map((agg, idx) => (
                                            <div key={agg.name} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl">
                                                <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold shrink-0">
                                                    {idx + 1}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-bold text-zinc-800">{agg.name}</p>
                                                    <p className="text-xs text-zinc-500">{agg.count} item occurrences</p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Price & Discount Movement Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Price Movement */}
                            <div className="bg-white p-6 rounded-2xl border border-zinc-100 shadow-lg shadow-zinc-200/30">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-teal-50 text-teal-600 rounded-xl">
                                        <TrendingUp className="w-4 h-4" />
                                    </div>
                                    <p className="text-sm font-bold text-zinc-700">Price Movement (Approved)</p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                                        <div className="flex items-center gap-2 mb-1">
                                            <ArrowUp className="w-4 h-4 text-emerald-600" />
                                            <span className="text-xs font-bold text-emerald-700">Price Increases</span>
                                        </div>
                                        <p className="text-2xl font-black text-emerald-700">{priceMovement.upCount}</p>
                                    </div>
                                    <div className="bg-rose-50 p-4 rounded-xl border border-rose-100">
                                        <div className="flex items-center gap-2 mb-1">
                                            <ArrowDown className="w-4 h-4 text-rose-600" />
                                            <span className="text-xs font-bold text-rose-700">Price Decreases</span>
                                        </div>
                                        <p className="text-2xl font-black text-rose-700">{priceMovement.downCount}</p>
                                    </div>
                                </div>
                                <div className="mt-4 flex gap-4">
                                    <div className="flex-1 text-center p-3 bg-zinc-50 rounded-xl">
                                        <p className="text-xs text-zinc-500 font-bold">Avg Change</p>
                                        <p className="text-lg font-bold text-zinc-800">{currency} {priceMovement.avgChange.toFixed(2)}</p>
                                    </div>
                                    <div className="flex-1 text-center p-3 bg-zinc-50 rounded-xl">
                                        <p className="text-xs text-zinc-500 font-bold">Avg % Change</p>
                                        <p className="text-lg font-bold text-zinc-800">{priceMovement.avgPercentChange.toFixed(1)}%</p>
                                    </div>
                                </div>
                            </div>

                            {/* Summary Stats */}
                            <div className="bg-white p-6 rounded-2xl border border-zinc-100 shadow-lg shadow-zinc-200/30">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                                        <CheckCircle className="w-4 h-4" />
                                    </div>
                                    <p className="text-sm font-bold text-zinc-700">Approved Summary</p>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                                        <p className="text-xs text-emerald-700 font-bold mb-1">Submissions</p>
                                        <p className="text-3xl font-black text-emerald-700">{approvedSubmissions.length}</p>
                                    </div>
                                    <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                                        <p className="text-xs text-blue-700 font-bold mb-1">Items</p>
                                        <p className="text-3xl font-black text-blue-700">{approvedItems.length}</p>
                                    </div>
                                    <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                                        <p className="text-xs text-purple-700 font-bold mb-1">Campaigns</p>
                                        <p className="text-3xl font-black text-purple-700">{Object.keys(approvedByCampaign).length}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Campaign Table (Approved Only) */}
                        <div className="bg-white rounded-2xl border border-zinc-100 shadow-lg shadow-zinc-200/30 overflow-hidden">
                            <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3 bg-zinc-50/50">
                                <div className="p-2 bg-white rounded-xl border border-zinc-200">
                                    <Megaphone className="w-4 h-4 text-zinc-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-zinc-800">Campaign Performance</h3>
                                    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Approved submissions only</p>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-zinc-50 text-zinc-500 font-bold text-xs uppercase tracking-wider border-b border-zinc-100">
                                        <tr>
                                            <th className="text-left px-6 py-3">Campaign</th>
                                            <th className="text-left px-6 py-3">Brands</th>
                                            <th className="text-center px-6 py-3">Approved</th>
                                            <th className="text-center px-6 py-3">Items</th>
                                            <th className="text-left px-6 py-3">Top Aggregator</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-50">
                                        {campaignTableData.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-8 text-center text-zinc-400">No approved campaigns found.</td>
                                            </tr>
                                        ) : (
                                            campaignTableData.map((row) => (
                                                <tr key={row.campaignName} className="hover:bg-zinc-50 transition-colors">
                                                    <td className="px-6 py-4 font-bold text-zinc-800">{row.campaignName}</td>
                                                    <td className="px-6 py-4 text-zinc-600 max-w-[200px] truncate">
                                                        {row.brands.slice(0, 2).join(', ')}{row.brands.length > 2 ? ` +${row.brands.length - 2}` : ''}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 font-bold text-sm">
                                                            {row.approvedSubmissions}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center font-bold text-zinc-700">{row.totalItems}</td>
                                                    <td className="px-6 py-4">
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-bold">
                                                            <Globe className="w-3 h-3" /> {row.topAggregator}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* PERFORMANCE TAB */}
            {activeTab === 'performance' && (
                <div className={`space-y-6 transition-all duration-200 ${transitioning ? 'opacity-50 scale-[0.995]' : 'opacity-100 scale-100'}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* BRAND PERFORMANCE CHART */}
                        <div className="bg-white p-8 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 flex flex-col">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="bg-teal-50 p-3 rounded-2xl text-teal-600 border border-teal-100">
                                    <BarChart2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-zinc-900 text-lg">Top Brands by Volume</h3>
                                    <p className="text-xs text-zinc-400 font-bold uppercase tracking-wider mt-0.5">Performance Metrics</p>
                                </div>
                            </div>
                            
                            <div className="space-y-6 flex-1">
                                {sortedBrands.length === 0 ? (
                                    <div className="h-full flex items-center justify-center text-zinc-400 italic">No data available</div>
                                ) : (
                                    sortedBrands.map((brandRow, idx) => {
                                        const brand = (brandRow as any).brand;
                                        const count = Number((brandRow as any).count || 0);
                                        const percentage = maxBrandCount > 0 ? (count / maxBrandCount) * 100 : 0;
                                        return (
                                            <div key={brand || idx} className="group">
                                                <div className="flex justify-between text-sm font-bold text-zinc-700 mb-2">
                                                    <span className="flex items-center gap-2">
                                                        <span className="w-5 h-5 rounded-full bg-zinc-100 text-zinc-500 flex items-center justify-center text-[10px]">{idx + 1}</span>
                                                        {brand || 'Brand'}
                                                    </span>
                                                    <span className="text-teal-600 bg-teal-50 px-2 py-0.5 rounded-md text-xs">{count} Items</span>
                                                </div>
                                                <div className="h-3 w-full bg-zinc-50 rounded-full overflow-hidden border border-zinc-100">
                                                    <div 
                                                        className="h-full bg-teal-500 rounded-full transition-all duration-1000 ease-out group-hover:bg-teal-600 shadow-lg shadow-teal-200"
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        </div>

                        {/* PLATFORM DISTRIBUTION */}
                        <div className="bg-white p-8 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 flex flex-col">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="bg-purple-50 p-3 rounded-2xl text-purple-600 border border-purple-100">
                                    <PieChart className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-zinc-900 text-lg">Platform Distribution</h3>
                                    <p className="text-xs text-zinc-400 font-bold uppercase tracking-wider mt-0.5">Channel Mix</p>
                                </div>
                            </div>

                            <div className="space-y-5">
                                {aggregators.filter(agg => agg.active).map(agg => {
                                    const count = platformCounts[agg.name] || 0;
                                    const percentage = totalPlatformTags > 0 ? (count / totalPlatformTags) * 100 : 0;
                                    
                                    return (
                                        <div key={agg.id} className="flex items-center gap-4 group">
                                            <div className="w-28 font-bold text-zinc-600 text-sm shrink-0">{agg.name}</div>
                                            <div className="flex-1 h-10 bg-zinc-50 rounded-xl overflow-hidden flex items-center relative border border-zinc-100 group-hover:border-purple-100 transition-colors">
                                                <div 
                                                    className="h-full bg-purple-500 absolute left-0 top-0 opacity-10 group-hover:opacity-20 transition-opacity"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                                <div 
                                                    className="h-full bg-purple-500 absolute left-0 top-0 w-1.5"
                                                />
                                                <span className="relative z-10 text-xs font-bold text-zinc-700 pl-4 flex justify-between w-full pr-4">
                                                    <span>{count} items</span>
                                                    <span className="text-zinc-400">{percentage.toFixed(1)}%</span>
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    {/* MANAGER LEADERBOARD */}
                    <div className="bg-white rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 overflow-hidden">
                        <div className="px-8 py-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/30">
                            <div className="flex items-center gap-4">
                                <div className="bg-white p-2.5 rounded-xl text-zinc-900 border border-zinc-200 shadow-sm">
                                    <Users className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-zinc-900 text-lg">Manager Leaderboard</h3>
                                    <p className="text-xs text-zinc-400 font-bold uppercase tracking-wider mt-0.5">Top Performers</p>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-zinc-50/50 text-zinc-400 font-bold uppercase text-[11px] tracking-wider border-b border-zinc-100">
                                    <tr>
                                        <th className="px-8 py-4">Rank</th>
                                        <th className="px-8 py-4">Manager Name</th>
                                        <th className="px-8 py-4">Total Submissions</th>
                                        <th className="px-8 py-4">Items Volume</th>
                                        <th className="px-8 py-4">Efficiency (Appr. Rate)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-50">
                                    {leaderboard.length === 0 ? (
                                        <tr><td colSpan={5} className="p-8 text-center text-zinc-400">No manager data available yet.</td></tr>
                                    ) : (
                                        leaderboard.map((m, i) => (
                                            <tr key={i} className="hover:bg-zinc-50 transition-colors group">
                                                <td className="px-8 py-5 font-bold text-zinc-400 group-hover:text-zinc-900">#{i + 1}</td>
                                                <td className="px-8 py-5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-gradient-to-br from-zinc-100 to-zinc-200 text-zinc-600 rounded-full flex items-center justify-center font-bold text-sm shadow-inner">
                                                            {m.name.charAt(0)}
                                                        </div>
                                                        <span className="font-bold text-zinc-800 text-base">{m.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5 font-bold text-zinc-600">{m.submissions}</td>
                                                <td className="px-8 py-5 font-bold text-zinc-900">{m.totalItems} items</td>
                                                <td className="px-8 py-5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-24 h-2.5 bg-zinc-100 rounded-full overflow-hidden shadow-inner">
                                                            <div className={`h-full rounded-full shadow-sm ${m.efficiency >= 80 ? 'bg-emerald-500' : m.efficiency >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${m.efficiency}%` }} />
                                                        </div>
                                                        <span className="font-bold text-xs text-zinc-500">{m.efficiency.toFixed(0)}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* FINANCIALS TAB */}
            {activeTab === 'financials' && (
                <div className={`space-y-6 transition-all duration-200 ${transitioning ? 'opacity-50 scale-[0.995]' : 'opacity-100 scale-100'}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Funding Impact Main Card */}
                        <div className="lg:col-span-2 bg-white p-8 rounded-3xl border border-zinc-100 shadow-xl shadow-zinc-200/40 relative overflow-hidden">
                            <div className="flex justify-between items-start mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-orange-50 text-orange-600 rounded-2xl border border-orange-100">
                                        <PieChart className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-zinc-900 text-lg">Funding Distribution</h3>
                                        <p className="text-xs text-zinc-400 font-bold uppercase tracking-wider mt-0.5">Cost Sharing Analysis</p>
                                    </div>
                                </div>
                                <span className="text-xs font-bold text-orange-600 bg-orange-50 px-3 py-1.5 rounded-full border border-orange-100">
                                    Financials
                                </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Aggregate Discount Share</p>
                                    <div className="flex items-baseline gap-2">
                                        <h3 className="text-4xl font-black text-zinc-900">
                                            {fundingImpact?.sterlingTotal?.toFixed(2) ?? '0'}
                                        </h3>
                                        <span className="text-sm font-bold text-zinc-500">Sterling</span>
                                    </div>
                                    <div className="w-full h-2 bg-zinc-100 rounded-full mt-2 mb-4 overflow-hidden">
                                        <div className="h-full bg-zinc-900 rounded-full" style={{ width: '40%' }}></div>
                                    </div>
                                    
                                    <div className="flex items-baseline gap-2 mt-6">
                                        <h3 className="text-4xl font-black text-zinc-400">
                                            {fundingImpact?.cofundTotal?.toFixed(2) ?? '0'}
                                        </h3>
                                        <span className="text-sm font-bold text-zinc-400">Co-Funded</span>
                                    </div>
                                    <p className="text-xs text-zinc-400 mt-4 leading-relaxed">
                                        Values represent percent-points or amount-equivalent across all active campaigns.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div className="bg-orange-50/50 rounded-2xl p-5 border border-orange-100">
                                        <p className="text-[11px] font-bold text-orange-700 uppercase mb-3 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                                            Discount Types
                                        </p>
                                        <div className="space-y-2">
                                            {Object.entries(fundingImpact?.discountTypeBreakdown || {}).map(([k, v]) => (
                                                <div key={k} className="flex justify-between items-center text-sm">
                                                    <span className="text-orange-900/70 font-medium">{k}</span>
                                                    <span className="font-bold text-orange-700 bg-white px-2 py-0.5 rounded-md shadow-sm">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="bg-amber-50/50 rounded-2xl p-5 border border-amber-100">
                                        <p className="text-[11px] font-bold text-amber-700 uppercase mb-3 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                            Funding Types
                                        </p>
                                        <div className="space-y-2">
                                            {Object.entries(fundingImpact?.fundingTypeBreakdown || {}).map(([k, v]) => (
                                                <div key={k} className="flex justify-between items-center text-sm">
                                                    <span className="text-amber-900/70 font-medium">{k}</span>
                                                    <span className="font-bold text-amber-700 bg-white px-2 py-0.5 rounded-md shadow-sm">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Discount Type Overview Cards */}
                        <div className="space-y-4">
                            {(fundingImpact?.discountTypeSummary || []).map((row) => (
                                <div key={row.discountType} className="bg-white border border-zinc-100 rounded-3xl p-6 shadow-xl shadow-zinc-200/40 hover:border-zinc-200 transition-all group">
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">{row.discountType || 'Unknown'} Discounts</p>
                                        <div className="w-8 h-8 rounded-full bg-zinc-50 flex items-center justify-center group-hover:bg-zinc-100 transition-colors">
                                            <DollarSign className="w-4 h-4 text-zinc-400 group-hover:text-zinc-600" />
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-zinc-500">Active Campaigns</span>
                                            <span className="text-lg font-bold text-zinc-900">{row.campaignCount}</span>
                                        </div>
                                        <div className="w-full h-px bg-zinc-100"></div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-zinc-500">Avg Value</span>
                                            <span className="text-lg font-bold text-emerald-600">{(row.averageDiscountValue || 0).toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            {(!fundingImpact?.discountTypeSummary || fundingImpact.discountTypeSummary.length === 0) && (
                                <div className="h-full bg-zinc-50 rounded-3xl border border-zinc-100 border-dashed flex items-center justify-center p-8 text-center">
                                    <p className="text-zinc-400 text-sm font-medium">No detailed discount data available for current period.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
